import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Title3, Button } from '@fluentui/react-components';
import { Add20Regular } from '@fluentui/react-icons';
import ProjectGrid from './components/ProjectGrid';
import { useProject } from '../../hooks/useProject';
import { Project } from '../../types';
import ConfirmDialog from '../../components/ConfirmDialog';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXL,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  content: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
    padding: tokens.spacingHorizontalXXL,
  },
});

const ProjectManagement: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [projectToDelete, setProjectToDelete] = React.useState<Project | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const { projects, listLoading, listError, deleteProject, deleteError, fetchProjects } = useProject();

  // Carregar projetos ao montar o componente
  React.useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleAdd = () => {
    navigate('/projects/new');
  };

  const handleEdit = (project: Project) => {
    navigate(`/projects/edit?id=${project.id}`);
  };

  const handleDelete = (id: number) => {
    const project = projects.find((p: Project) => p.id === id);
    if (project) {
      setProjectToDelete(project);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (projectToDelete) {
      try {
        await deleteProject(projectToDelete.id);
        // Fechar o dialog de confirmação
        setDeleteDialogOpen(false);
        setProjectToDelete(null);
        // Após exclusão bem-sucedida, atualizar a lista
        fetchProjects();
      } catch (error) {
        // Fechar o dialog de confirmação
        setDeleteDialogOpen(false);
        setProjectToDelete(null);
        // Erro será tratado pelo estado error do hook
        console.error('Erro ao excluir projeto:', error);
      }
    }
  };

  // Efeito para monitorar erro de exclusão e exibir dialog de alerta
  React.useEffect(() => {
    if (deleteError) {
      // Verificar se é um erro 400 (validação)
      if (deleteError.statusCode === 400) {
        setErrorMessage(deleteError.message || 'Não foi possível excluir o projeto.');
        setErrorDialogOpen(true);
      } else {
        // Para outros erros, apenas logar
        console.error('Erro ao excluir projeto:', deleteError.message);
      }
    }
  }, [deleteError]);

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title3 className={styles.title}>Projetos</Title3>
        <Button
          appearance="primary"
          size="large"
          icon={<Add20Regular />}
          onClick={handleAdd}
          className={styles.addButton}
        >
          Novo Projeto
        </Button>
      </div>

      <div className={styles.content}>
        <ProjectGrid
          projects={projects}
          loading={listLoading}
          error={listError?.message || null}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Excluir Projeto"
        message={`Tem certeza que deseja excluir o projeto "${projectToDelete?.descricao}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        intent="danger"
      />

      <ConfirmDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Não foi possível excluir"
        message={errorMessage}
        confirmText="OK"
        onConfirm={() => setErrorDialogOpen(false)}
        mode="alert"
      />
    </div>
  );
};

export default ProjectManagement;